// Copyright (c) Ping. All rights reserved.
//
// Single source of truth for the `kind` string ↔ AppEventType numeric
// tag mapping. The current wire format wraps every send as
//   { kind: "com.ping.X", payload: {...} }
// while `docs/architecture/types-and-schemas.md` §1.1 prescribes a
// CBOR envelope with the integer tag in key `2`. The mapping below
// is the only place the two encodings need to agree — every other
// renderer / preview formatter consumes the AppEventType numeric tag
// directly.

import {
  AffordanceType,
  CallType,
  ContentType,
  GameType,
  GroupStateType,
  MemberType,
  MutationType,
  PersonalType,
  PinningType,
  PollType,
  ScheduledType,
  type AppEventType,
} from './types';

/**
 * Kind strings used on the wire today. Any string outside this union
 * is treated as `unrenderable` by the dispatch layer (`docs/...` §1.3
 * forward-compatibility rule).
 */
export type AppEventKind =
  | 'com.ping.text'
  | 'com.ping.image'
  | 'com.ping.video'
  | 'com.ping.voice'
  | 'com.ping.file'
  | 'com.ping.location'
  | 'com.ping.sticker'
  | 'com.ping.gif'
  | 'com.ping.link_preview'
  | 'com.ping.edit'
  | 'com.ping.redact'
  | 'com.ping.forward'
  | 'com.ping.reaction'
  | 'com.ping.reaction_remove'
  | 'com.ping.thread_root'
  | 'com.ping.group_name'
  | 'com.ping.group_avatar'
  | 'com.ping.group_topic'
  | 'com.ping.invite'
  | 'com.ping.kick'
  | 'com.ping.pin'
  | 'com.ping.unpin'
  | 'com.ping.save'
  | 'com.ping.unsave'
  | 'com.ping.star'
  | 'com.ping.blocked_user_upsert'
  | 'com.ping.blocked_user_remove'
  | 'com.ping.disappearing_policy'
  | 'com.ping.member_role'
  | 'com.ping.poll'
  | 'com.ping.poll_vote'
  | 'com.ping.note_upsert'
  | 'com.ping.note_delete'
  | 'com.ping.task_upsert'
  | 'com.ping.task_delete'
  | 'com.ping.self_profile'
  | 'com.ping.conv_state'
  | 'com.ping.read_pointer'
  | 'com.ping.tour_seen'
  | 'com.ping.reminder_set'
  | 'com.ping.reminder_clear'
  | 'com.ping.game_create'
  | 'com.ping.game_join'
  | 'com.ping.game_move'
  | 'com.ping.game_reveal'
  | 'com.ping.game_outcome'
  | 'com.ping.game_abort'
  | 'com.ping.call_invite'
  | 'com.ping.call_accept'
  | 'com.ping.call_decline'
  | 'com.ping.call_end'
  | 'com.ping.call_ringing'
  | 'com.ping.call_hand_raise'
  | 'com.ping.call_reaction'
  | 'com.ping.call_roster'
  | 'com.ping.call_handoff'
  | 'com.ping.meeting'
  | 'com.ping.meeting_update'
  | 'com.ping.meeting_rsvp'
  | 'com.ping.meeting_cancel';

/**
 * Sidecar kinds that are not chat-room renderable but live inside
 * the same Application envelope channel today. They drive sidecar
 * mutations only — receipts on existing rows, profile bootstrap.
 * Kept out of the renderable `AppEventKind` union so the variant +
 * preview registries can guarantee `kind` is dispatchable.
 */
export type SidecarKind =
  | 'com.ping.delivered'
  | 'com.ping.read'
  | 'ping.profile';

const KIND_TO_TYPE: { readonly [K in AppEventKind]: AppEventType } = {
  'com.ping.text': ContentType.TEXT,
  'com.ping.image': ContentType.IMAGE,
  'com.ping.video': ContentType.VIDEO,
  'com.ping.voice': ContentType.VOICE,
  'com.ping.file': ContentType.FILE,
  'com.ping.location': ContentType.LOCATION_ONCE,
  'com.ping.sticker': ContentType.STICKER,
  // GIFs ride the wire under their own kind today but share the
  // IMAGE numeric tag — the doc spec doesn't yet allocate a separate
  // tag for animated images. CBOR migration can split this if needed.
  'com.ping.gif': ContentType.IMAGE,
  'com.ping.link_preview': ContentType.LINK_PREVIEW,
  'com.ping.edit': MutationType.EDIT,
  'com.ping.redact': MutationType.REDACT,
  'com.ping.forward': MutationType.FORWARD,
  'com.ping.reaction': AffordanceType.REACTION,
  'com.ping.reaction_remove': AffordanceType.REACTION_REMOVE,
  'com.ping.thread_root': AffordanceType.THREAD_ROOT,
  'com.ping.group_name': GroupStateType.GROUP_NAME,
  'com.ping.group_avatar': GroupStateType.GROUP_AVATAR,
  'com.ping.group_topic': GroupStateType.GROUP_TOPIC,
  'com.ping.invite': MemberType.INVITE,
  'com.ping.kick': MemberType.KICK_REASON,
  'com.ping.pin': PinningType.PIN,
  'com.ping.unpin': PinningType.UNPIN,
  'com.ping.save': PinningType.SAVE,
  'com.ping.unsave': PinningType.UNSAVE,
  'com.ping.star': PinningType.STAR,
  'com.ping.blocked_user_upsert': PersonalType.BLOCKED_USER_UPSERT,
  'com.ping.blocked_user_remove': PersonalType.BLOCKED_USER_REMOVE,
  'com.ping.disappearing_policy': ScheduledType.DISAPPEARING_POLICY,
  'com.ping.member_role': MemberType.MEMBER_ROLE_SET,
  'com.ping.poll': PollType.POLL_CREATE,
  'com.ping.poll_vote': PollType.POLL_VOTE,
  'com.ping.note_upsert': PersonalType.NOTE_UPSERT,
  'com.ping.note_delete': PersonalType.NOTE_DELETE,
  'com.ping.task_upsert': PersonalType.TASK_UPSERT,
  'com.ping.task_delete': PersonalType.TASK_DELETE,
  'com.ping.self_profile': PersonalType.SELF_PROFILE,
  'com.ping.conv_state': PersonalType.CONV_STATE,
  'com.ping.read_pointer': PersonalType.READ_POINTER,
  'com.ping.tour_seen': PersonalType.TOUR_SEEN,
  'com.ping.reminder_set': PersonalType.REMINDER_SET,
  'com.ping.reminder_clear': PersonalType.REMINDER_CLEAR,
  'com.ping.game_create': GameType.GAME_CREATE,
  'com.ping.game_join': GameType.GAME_JOIN,
  'com.ping.game_move': GameType.GAME_MOVE,
  'com.ping.game_reveal': GameType.GAME_REVEAL,
  'com.ping.game_outcome': GameType.GAME_OUTCOME,
  'com.ping.game_abort': GameType.GAME_ABORT,
  'com.ping.call_invite': CallType.CALL_INVITE,
  'com.ping.call_accept': CallType.CALL_ACCEPT,
  'com.ping.call_decline': CallType.CALL_DECLINE,
  'com.ping.call_end': CallType.CALL_END,
  'com.ping.call_ringing': CallType.CALL_RINGING,
  'com.ping.call_hand_raise': CallType.CALL_HAND_RAISE,
  'com.ping.call_reaction': CallType.CALL_REACTION,
  'com.ping.call_roster': CallType.CALL_ROSTER,
  'com.ping.call_handoff': CallType.CALL_HANDOFF,
  'com.ping.meeting': ScheduledType.MEETING_CREATE,
  'com.ping.meeting_update': ScheduledType.MEETING_UPDATE,
  'com.ping.meeting_rsvp': ScheduledType.MEETING_RSVP,
  'com.ping.meeting_cancel': ScheduledType.MEETING_CANCEL,
};

// Kinds that share a numeric type tag with another canonical kind
// (e.g. `com.ping.gif` rides on `ContentType.IMAGE = 0x11`). Excluded
// from the reverse lookup so `kindForAppEventType(0x11)` always
// returns the canonical `'com.ping.image'`.
const NON_CANONICAL_KINDS: ReadonlyArray<AppEventKind> = ['com.ping.gif'];

const TYPE_TO_KIND: ReadonlyMap<AppEventType, AppEventKind> = (() => {
  const m = new Map<AppEventType, AppEventKind>();
  for (const k of Object.keys(KIND_TO_TYPE) as AppEventKind[]) {
    if (NON_CANONICAL_KINDS.includes(k)) continue;
    m.set(KIND_TO_TYPE[k], k);
  }
  return m;
})();

/** Numeric tag for a wire kind. Returns `undefined` for unknown kinds. */
export function appEventTypeForKind(
  kind: string,
): AppEventType | undefined {
  if (Object.prototype.hasOwnProperty.call(KIND_TO_TYPE, kind)) {
    return KIND_TO_TYPE[kind as AppEventKind];
  }
  return undefined;
}

/** Wire kind for a numeric tag. Useful for CBOR → JSON migration. */
export function kindForAppEventType(
  type: AppEventType,
): AppEventKind | undefined {
  return TYPE_TO_KIND.get(type);
}

/** Type guard: is the string a renderable kind we know about? */
export function isAppEventKind(s: unknown): s is AppEventKind {
  return typeof s === 'string' && Object.prototype.hasOwnProperty.call(KIND_TO_TYPE, s);
}

/** Type guard: is the string a sidecar (receipt / profile) kind? */
export function isSidecarKind(s: unknown): s is SidecarKind {
  return (
    s === 'com.ping.delivered' ||
    s === 'com.ping.read' ||
    s === 'ping.profile'
  );
}
